import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RefreshCw, AlertCircle, ArrowLeft, Copy, Check, Tag, Link } from 'lucide-react';
import Alert, { AlertDescription } from '../components/ui/alert';

const API_BASE = 'https://api.notely.se';
const DASHBOARD_SECRET = process.env.REACT_APP_DASHBOARD_SECRET;

interface PromoCode {
  id: number;
  code: string;
  plan: 'premium_monthly_1' | 'premium_monthly_2';
  duration_months: number;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  is_active: number;
  created_at: string;
}

const PLAN_LABELS: Record<string, string> = {
  premium_monthly_1: 'Basic',
  premium_monthly_2: 'Standard',
};

function generateRandomCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

const PromoCodes: React.FC = () => {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedUrlId, setCopiedUrlId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Form state
  const [formCode, setFormCode] = useState('');
  const [formPlan, setFormPlan] = useState<'premium_monthly_1' | 'premium_monthly_2'>('premium_monthly_1');
  const [formDuration, setFormDuration] = useState(1);
  const [formMaxUses, setFormMaxUses] = useState(1);
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/promo-codes`, {
        headers: { 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCodes(data.codes ?? []);
    } catch (e: any) {
      setError(e.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!formCode.trim()) {
      setFormError('Code is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({
          code: formCode.trim().toUpperCase(),
          plan: formPlan,
          durationMonths: formDuration,
          maxUses: formMaxUses,
          expiresAt: formExpiresAt || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setFormCode('');
      setFormPlan('premium_monthly_1');
      setFormDuration(1);
      setFormMaxUses(1);
      setFormExpiresAt('');
      setSuccessMsg('Promo code created successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchCodes();
    } catch (e: any) {
      setFormError(e.message || 'Failed to create promo code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (code: PromoCode) => {
    setTogglingId(code.id);
    try {
      const res = await fetch(`${API_BASE}/dashboard/promo-codes/${code.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({ isActive: !code.is_active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchCodes();
    } catch (e: any) {
      setError(e.message || 'Failed to update promo code');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCopyUrl = async (code: PromoCode) => {
    try {
      await navigator.clipboard.writeText(`https://notely.se/code/${code.code}`);
      setCopiedUrlId(code.id);
      setTimeout(() => setCopiedUrlId(null), 2000);
    } catch {
      // ignore
    }
  };

  const handleCopy = async (code: PromoCode) => {
    try {
      await navigator.clipboard.writeText(code.code);
      setCopiedId(code.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <a href="/dashboard-process-logs" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <Tag className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold">Promo Codes</h1>
          </div>
          <button
            onClick={fetchCodes}
            disabled={loading}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <Alert className="mb-4 border-red-500 bg-red-900/20">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-red-300">{error}</AlertDescription>
          </Alert>
        )}

        {successMsg && (
          <Alert className="mb-4 border-green-500 bg-green-900/20">
            <Check className="h-4 w-4 text-green-400" />
            <AlertDescription className="text-green-300">{successMsg}</AlertDescription>
          </Alert>
        )}

        {/* Create Form */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-lg">Create Promo Code</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Code */}
              <div className="lg:col-span-1">
                <label className="block text-sm text-gray-400 mb-1">Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formCode}
                    onChange={e => setFormCode(e.target.value.toUpperCase())}
                    placeholder="E.g. PROMO2026"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                  <button
                    type="button"
                    onClick={() => setFormCode(generateRandomCode())}
                    className="bg-gray-600 hover:bg-gray-500 px-3 py-2 rounded-lg text-xs transition-colors whitespace-nowrap"
                  >
                    Random
                  </button>
                </div>
              </div>

              {/* Plan */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Plan</label>
                <select
                  value={formPlan}
                  onChange={e => setFormPlan(e.target.value as any)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                >
                  <option value="premium_monthly_1">Basic</option>
                  <option value="premium_monthly_2">Standard</option>
                </select>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Duration (months)</label>
                <input
                  type="number"
                  min={1}
                  value={formDuration}
                  onChange={e => setFormDuration(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Max Uses */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Max uses</label>
                <input
                  type="number"
                  min={1}
                  value={formMaxUses}
                  onChange={e => setFormMaxUses(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Expires At */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Expires at (optional)</label>
                <input
                  type="date"
                  value={formExpiresAt}
                  onChange={e => setFormExpiresAt(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Submit */}
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {submitting ? 'Creating…' : 'Create Code'}
                </button>
              </div>

              {formError && (
                <div className="sm:col-span-2 lg:col-span-3 text-red-400 text-sm">{formError}</div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Codes Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white text-lg">
              All Codes {!loading && <span className="text-gray-400 font-normal text-sm">({codes.length})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : codes.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No promo codes yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-700">
                      <th className="text-left py-3 px-2">Code</th>
                      <th className="text-left py-3 px-2">Plan</th>
                      <th className="text-left py-3 px-2">Duration</th>
                      <th className="text-left py-3 px-2">Used / Max</th>
                      <th className="text-left py-3 px-2">Expires</th>
                      <th className="text-left py-3 px-2">Created</th>
                      <th className="text-left py-3 px-2">Status</th>
                      <th className="text-left py-3 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map(code => (
                      <tr key={code.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">{code.code}</span>
                            <button
                              onClick={() => handleCopy(code)}
                              className="text-gray-500 hover:text-gray-300 transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedId === code.id ? (
                                <Check className="w-3.5 h-3.5 text-green-400" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleCopyUrl(code)}
                              className="text-gray-500 hover:text-gray-300 transition-colors"
                              title="Copy deep link URL"
                            >
                              {copiedUrlId === code.id ? (
                                <Check className="w-3.5 h-3.5 text-green-400" />
                              ) : (
                                <Link className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-gray-300">{PLAN_LABELS[code.plan] ?? code.plan}</td>
                        <td className="py-3 px-2 text-gray-300">{code.duration_months}mo</td>
                        <td className="py-3 px-2 text-gray-300">{code.used_count} / {code.max_uses}</td>
                        <td className="py-3 px-2 text-gray-300">
                          {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-3 px-2 text-gray-400 text-xs">
                          {new Date(code.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-2">
                          {code.is_active ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400 border border-green-700/50">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700/50 text-gray-400 border border-gray-600/50">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <button
                            onClick={() => handleToggle(code)}
                            disabled={togglingId === code.id}
                            className={`text-xs px-3 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                              code.is_active
                                ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-700/40'
                                : 'bg-green-900/30 hover:bg-green-900/50 text-green-400 border border-green-700/40'
                            }`}
                          >
                            {togglingId === code.id ? '…' : code.is_active ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PromoCodes;
